import { env } from '../config/env';
import { getAuthTokenProvider } from './auth-token-provider';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
}

async function getAuthToken(): Promise<string | null> {
  const provider = getAuthTokenProvider();
  if (!provider) return null;
  return provider.getToken();
}

function getImpersonationCompanyId(): string | undefined {
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (!authStorage) return undefined;
    const parsed = JSON.parse(authStorage);
    if (parsed?.state?.impersonation?.isImpersonating) {
      return parsed.state.impersonation.impersonatedContext?.companyId;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export async function apiClient<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const token = await getAuthToken();
  const impersonationCompanyId = getImpersonationCompanyId();
  const url = `${env.VITE_API_URL}${endpoint}`;

  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(impersonationCompanyId ? { 'X-Impersonation-Company': impersonationCompanyId } : {}),
      ...headers,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export async function apiUpload<T>(endpoint: string, formData: FormData): Promise<T> {
  const token = await getAuthToken();
  const impersonationCompanyId = getImpersonationCompanyId();
  const url = `${env.VITE_API_URL}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(impersonationCompanyId ? { 'X-Impersonation-Company': impersonationCompanyId } : {}),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
